import { finalize, Observable } from "rxjs";
import { CORE_API } from "../constants";
import { UserModel } from "../models/user.model";
import { AppVersionFeatureModel, AppVersionModel, BaseResponseModel, SelectListItem } from "../models"
import { Inject, Injectable } from "@angular/core";
import { UserAuthInterface } from "./user-auth.interface";
import { HTTP_SERVICE, HttpInterface } from "./http.interface";
import { ResetPasswordModel } from "app/account/model";
import { HttpParams } from "@angular/common/http";
import { ProductInventoryDataListModel } from "app/pages/product-inventory/models";
import { StoreUserDetailsModel } from "app/account/model/user-details-by-token.model";
import { RegistrationModel } from "app/account/model/registration.model";
@Injectable()
export class UserAuthService implements UserAuthInterface {

    //#region Constants

    private AUTH_USER_KEY_NAME = "kisna.pos.auth.usp";
    private REMEMBERED_USERNAME_KEY = 'kisna.pos.remember.username';
    //#endregion

    //#region Constructor

    constructor(@Inject(HTTP_SERVICE) private _httpInterface: HttpInterface) { }


    //#endregion

    //#region Properties

    private _currentAuthUser: UserModel;


    get authenticatedUserPermissions(): string[] {
        let authUser = this.authenticatedUser;
        return authUser?.permissions ?? Array<string>();
    }

    get authenticatedUser(): UserModel {
        if (this._currentAuthUser) {
            return this._currentAuthUser;
        }

        let data = localStorage.getItem(this.AUTH_USER_KEY_NAME) || sessionStorage.getItem(this.AUTH_USER_KEY_NAME);
        if (!data) {
            return null;
        }

        this._currentAuthUser = new UserModel();
        Object.assign(this._currentAuthUser, JSON.parse(data));
        return this._currentAuthUser;
    }

    //#endregion


    //#region Methods

    login(Code: string, Password: string): Observable<BaseResponseModel<UserModel>> {
        return this._httpInterface.post<BaseResponseModel<UserModel>>(CORE_API.USER_LOGI_IN, { UserName: Code, Password: Password });
    }

    sendPasswordResetLink(email: string): Observable<BaseResponseModel<string>> {
        return this._httpInterface.post<BaseResponseModel<string>>(CORE_API.USER_FORGOT_PASSWORD, { email: email });
    }

    resetUserPassword(model: ResetPasswordModel): Observable<BaseResponseModel<UserModel>> {
        return this._httpInterface.post<BaseResponseModel<UserModel>>(CORE_API.USER_RESET_PASSWORD, model);
    }

    setAuthenticatedUser(user: UserModel, rememberMe: boolean): void {
        if (!user) {
            throw 'User authentication is null';
        }

        this._currentAuthUser = user;
        const storage = rememberMe ? localStorage : sessionStorage;
        storage.setItem(this.AUTH_USER_KEY_NAME, JSON.stringify(this._currentAuthUser));
    }

    setRememberedUsername(code: string): void {
        if (!code) return;
        localStorage.setItem(this.REMEMBERED_USERNAME_KEY, code);
    }

    getRememberedUsername(): string | null {
        return localStorage.getItem(this.REMEMBERED_USERNAME_KEY);
    }

    clearRememberedUsername(): void {
        localStorage.removeItem(this.REMEMBERED_USERNAME_KEY);
    }

    hasPermission(permissionName: string): boolean {
        if (!this._currentAuthUser || !this._currentAuthUser.permissions) {
            return false;
        }
        return this._currentAuthUser.permissions.some(item => item === permissionName);
    }

    // logoutUser(): void {
    //     this._currentAuthUser = null;
    //     localStorage.removeItem(this.AUTH_USER_KEY_NAME);
    //     sessionStorage.removeItem(this.AUTH_USER_KEY_NAME);
    // }

    // logoutUser() {
    //     return this._httpInterface.post(CORE_API.LOGOUT_USER, {}).pipe(
    //         finalize(() => {
    //             this._currentAuthUser = null;
    //             localStorage.removeItem(this.AUTH_USER_KEY_NAME);
    //             sessionStorage.removeItem(this.AUTH_USER_KEY_NAME);
    //         })
    //     );
    // }



    clearSession(): void {
        this._currentAuthUser = null;
        localStorage.removeItem(this.AUTH_USER_KEY_NAME);
        sessionStorage.removeItem(this.AUTH_USER_KEY_NAME);
    }

    logoutUser() {
        return this._httpInterface.post(CORE_API.LOGOUT_USER, {}).pipe(
            finalize(() => this.clearSession())
        );
    }

    getDirecotryMetaDataList(): Observable<BaseResponseModel<SelectListItem>> {
        return this._httpInterface.get(CORE_API.GET_DIRECTORY_META_DATA_LIST);
    }

    getAllProductInventory(searchText: string): Observable<BaseResponseModel<ProductInventoryDataListModel>> {
        let params = new HttpParams().set('searchText', searchText);
        return this._httpInterface.get(CORE_API.GLOBAL_SEARCH, params);
    }

    getUserDetailsByToken(token: string): Observable<BaseResponseModel<StoreUserDetailsModel>> {
        let param = new HttpParams().set('token', token);
        return this._httpInterface.get(CORE_API.GET_STORE_USER_DETAILS_BY_TOKEN, param);
    }

    insertUser(user: RegistrationModel): Observable<BaseResponseModel<string>> {
        return this._httpInterface.post(CORE_API.INSERT_USER, user);
    }


    // user-auth.service.ts

    //#region JWT helpers

    private decodeToken(token: string): any | null {
        try {
            const payload = token.split('.')[1];
            // base64url -> base64
            const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(
                atob(base64)
                    .split('')
                    .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                    .join('')
            );
            return JSON.parse(jsonPayload);
        } catch (error) {
            console.error('Token decode error', error);
            return null;
        }
    }

    isTokenExpired(token?: string): boolean {
        const t = token ?? this.authenticatedUser?.token;
        if (!t) return true;

        const decoded = this.decodeToken(t);
        if (!decoded?.exp) return true;

        // exp is in seconds, Date.now() is in ms
        return Date.now() >= decoded.exp * 1000;
    }

    getCurrentAppVersion(): Observable<BaseResponseModel<AppVersionModel>> {
        return this._httpInterface.get(CORE_API.GET_CURRENT_APP_VERSION);
    }

    getCurrentAppVersionFeatureByIf(id: string): Observable<BaseResponseModel<AppVersionFeatureModel>> {
        let params = new HttpParams()
            .set('appVersionId', id)
        return this._httpInterface.get(CORE_API.GET_CURRENT_APP_VERSION_FEATURE_BY_ID, params)
    }

    getAllAppVersions(): Observable<BaseResponseModel<AppVersionModel[]>> {
        return this._httpInterface.get(CORE_API.GET_ALL_APP_VERSIONS);
    }

    getAllAppVersionFeatures(): Observable<BaseResponseModel<AppVersionFeatureModel[]>> {
        return this._httpInterface.get(CORE_API.GET_ALL_APP_VERSION_FEATURES);
    }

    getSupportUserInfo(): Observable<BaseResponseModel<any>> {
        return this._httpInterface.get(CORE_API.GET_SUPPORT_USER_INFO);
    }

    //#endregion
}